'use client';

import { Monitor, Moon, Sun } from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';

import { Tooltip } from '@platform/ui/components/tooltip';
import { getThemePreference, setThemePreference, type ThemePreference } from '@platform/ui/lib/theme';
import { cn } from '@platform/ui/lib/utils';

const DARK_SCHEME = '(prefers-color-scheme: dark)';

function subscribeToSystemScheme(onChange: () => void) {
    const query = window.matchMedia(DARK_SCHEME);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
}

function systemIsDark() {
    return window.matchMedia(DARK_SCHEME).matches;
}

/**
 * Light, dark or the operating system's choice, for an app that offers both
 * themes (docs/10 §4, Theme switch). Switching sets one attribute on <html>;
 * the roles do the rest, so no component re-renders differently.
 */
export function ThemeToggle({ labels, className }: { labels: Record<ThemePreference, string> & { label: string }; className?: string }) {
    const [preference, setPreference] = useState<ThemePreference>(getThemePreference);
    // Following the system, the knob has to move when the system changes its mind.
    const systemDark = useSyncExternalStore(subscribeToSystemScheme, systemIsDark, () => false);
    const dark = preference === 'dark' || (preference === 'system' && systemDark);
    const Icon = preference === 'system' ? Monitor : dark ? Moon : Sun;
    const name = `${labels.label}: ${labels[preference]}`;

    function cycle() {
        // Three choices, two looks: one step of the cycle cannot repaint the page.
        // Leaving the system for its opposite puts that step last, so the first
        // click always visibly works.
        const system = systemDark ? 'dark' : 'light';
        const other = systemDark ? 'light' : 'dark';
        const next: ThemePreference = preference === 'system' ? other : preference === other ? system : 'system';
        setPreference(next);
        setThemePreference(next);
    }

    return (
        <Tooltip label={name} side="bottom" align="end">
            <button
                type="button"
                data-slot="theme-toggle"
                aria-label={name}
                onClick={cycle}
                className={cn('group inline-flex h-9 shrink-0 cursor-pointer items-center outline-none pointer-coarse:h-11', className)}
            >
                <span
                    aria-hidden="true"
                    className="relative flex h-8 w-14 items-center justify-between rounded-full border border-border bg-muted px-2 text-muted-foreground transition-colors group-hover:text-foreground group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background"
                >
                    <Sun className="size-4" />
                    <Moon className="size-4" />
                    <span
                        className={cn(
                            'absolute top-px left-px flex size-7 items-center justify-center rounded-full border border-input bg-card text-foreground motion-safe:transition-transform',
                            dark && 'translate-x-6',
                        )}
                    >
                        <Icon className="size-4" />
                    </span>
                </span>
            </button>
        </Tooltip>
    );
}
